/*=============================================== Common props ===============================================*/

import { Link } from "react-router-dom"
import type { PropsItemType } from "components"
import { typeValues } from "tsx-library-julseb/types"

export const iconBoxicon = (icon: string) => (
    <>
        icon <code>{icon}</code> from{" "}
        <a
            href={`https://boxicons.com/?query=${icon.replaceAll("-", " ")}`}
            target="_blank"
            rel="noreferrer noopener"
        >
            Boxicons
        </a>
    </>
)

export const commonProps = {
    allColors: (
        <>
            any <Link to="/styles/colors">color from our library</Link>.
        </>
    ),
    allColorsOverlays: (
        <>
            any <Link to="/styles/colors">color from our library</Link>, or any{" "}
            <Link to="/styles/overlays">overlay from our library</Link>.
        </>
    ),
    addIcon: (
        <>
            first, import your SVG icon in your folder{" "}
            <code>/public/icons</code>, and then put the name of your icon.
        </>
    ),
    addIconCapital: (
        <>
            First, import your SVG icon in your folder{" "}
            <code>/public/icons</code>, and then put the name of your icon.
        </>
    ),
    addIconFirst: (
        <>
            if you want to use an icon, first import your SVG icon in your
            folder <code>/public/icons</code>, and then put the name of your
            icon.
        </>
    ),
    introShadowObject: (
        <>
            If the <code>shadow</code> prop is an object, the three properties
            are required.
        </>
    ),
    to: {
        subtitle: "to",
        intro: (
            <>
                To use this component as a <code>React Router Link</code>.
            </>
        ),
        type: "string",
    },
    routerOnly: (
        <>
            This will work only with projects using{" "}
            <a
                href="https://reactrouter.com/en/main"
                target="_blank"
                rel="noreferrer noopener"
            >
                react-router-dom
            </a>
            .
        </>
    ),
    introTextColor:
        "Use this one only if you have a custom background color on your component.",
    colorsShort: [
        ...Object.keys(typeValues.colorsHover),
        ...Object.keys(typeValues.colorsInput),
        ...Object.keys(typeValues.colorsShort),
    ],
    colorsHover: Object.keys(typeValues.colorsHover),
    textAlign: Object.keys(typeValues.textAlign),
    spacers: [...Object.keys(typeValues.spacers), "any number"],
    allShadows: Object.keys(typeValues.shadows),
    allRadiuses: [...Object.keys(typeValues.radiuses), "any number"],
    allFontSizes: Object.keys(typeValues.fontSizes),
    allFontWeights: Object.keys(typeValues.weights),
    gridJustifyItems: Object.keys(typeValues.gridJustifyItems),
    gridAlignItems: Object.keys(typeValues.gridAlignItems),
    gridJustifyContent: Object.keys(typeValues.gridJustifyContent),
    gridAlignContent: Object.keys(typeValues.gridAlignContent),
    flexAlignItems: Object.keys(typeValues.flexAlignItems),
    flexJustifyContent: Object.keys(typeValues.flexJustifyContent),
    flexAlignContent: Object.keys(typeValues.flexAlignContent),
    flexJustifyItems: Object.keys(typeValues.flexJustifyItems),
    objectFit: Object.keys(typeValues.objectFit),
    validationInput: Object.keys(typeValues.validation),
    positions: Object.keys(typeValues.positions),
    backgroundInput: ["light", "dark"],
    backgroundInputDefault: (
        <>
            <code>light</code> for light theme, <code>dark</code> for dark
            theme.
        </>
    ),
}

type SeparatorType = "large" | "small"

const returnSeparator = (separator?: SeparatorType) => {
    const a: PropsItemType =
        separator === "small" ? "separator-small" : "separator"
    return a
}

export const paddingObject = (
    intro?: string | JSX.Element | undefined,
    defaultProp?: string | JSX.Element,
    separator?: SeparatorType
) => {
    const items: PropsItemType[] = [
        returnSeparator(separator),
        {
            subtitle: "padding",
            intro,
            type: "string, number or object",
            possible: [...commonProps.spacers, "object"],
            default: defaultProp,
        },
        {
            title: (
                <>
                    <code>padding</code> object
                </>
            ),
            subtitle: "left",
            type: "string or number",
            possible: commonProps.spacers,
        },
        {
            subtitle: "top",
            type: "string or number",
            possible: commonProps.spacers,
        },
        {
            subtitle: "right",
            type: "string or number",
            possible: commonProps.spacers,
        },
        {
            subtitle: "bottom",
            type: "string or number",
            possible: commonProps.spacers,
        },
        {
            subtitle: "leftRight",
            type: "string or number",
            possible: commonProps.spacers,
        },
        {
            subtitle: "topBottom",
            type: "string or number",
            possible: commonProps.spacers,
        },
    ]

    return items
}

export const radiusObject = (separator?: SeparatorType) => {
    const radius: PropsItemType[] = [
        returnSeparator(separator),
        {
            subtitle: "borderRadius",
            type: "string, number or object",
            possible: [...commonProps.allRadiuses, "object"],
        },
        {
            title: (
                <>
                    <code>borderRadius</code> object
                </>
            ),
            subtitle: "topLeft",
            type: "string or number",
            possible: commonProps.allRadiuses,
        },
        {
            subtitle: "topRight",
            type: "string or number",
            possible: commonProps.allRadiuses,
        },
        {
            subtitle: "bottomRight",
            type: "string or number",
            possible: commonProps.allRadiuses,
        },
        {
            subtitle: "bottomLeft",
            type: "string or number",
            possible: commonProps.allRadiuses,
        },
    ]

    return radius
}

export const shadowObject = (separator?: SeparatorType) => {
    const a: PropsItemType[] = [
        returnSeparator(separator),
        {
            subtitle: "shadow",
            type: "string or object",
            possible: [...commonProps.allShadows, "object"],
        },
        {
            title: (
                <>
                    <code>shadow</code> object
                </>
            ),
            subtitle: "default",
            intro: commonProps.introShadowObject,
            type: "string",
            possible: commonProps.allShadows,
        },
        {
            subtitle: "hover",
            type: "string",
            possible: commonProps.allShadows,
        },
        {
            subtitle: "active",
            type: "string",
            possible: commonProps.allShadows,
        },
    ]

    return a
}

export const positionObject = (separator?: SeparatorType) => {
    const a: PropsItemType[] = [
        returnSeparator(separator),
        {
            subtitle: "position",
            type: "object",
        },
        {
            title: (
                <>
                    <code>position</code> object
                </>
            ),
            subtitle: "position",
            type: "string",
            possible: commonProps.positions,
        },
        {
            subtitle: "left",
            type: "string or number",
            possible: commonProps.spacers,
        },
        {
            subtitle: "top",
            type: "string or number",
            possible: commonProps.spacers,
        },
        {
            subtitle: "right",
            type: "string or number",
            possible: commonProps.spacers,
        },
        {
            subtitle: "bottom",
            type: "string or number",
            possible: commonProps.spacers,
        },
        {
            subtitle: "zIndex",
            type: "number",
        },
    ]

    return a
}

export const propTo: PropsItemType = {
    subtitle: "to",
    intro: (
        <>
            To use this component as a{" "}
            <a
                href="https://reactrouter.com/en/main/components/link"
                target="_blank"
                rel="noreferrer noopener"
            >
                React Router Link
            </a>
            .
        </>
    ),
    type: "string",
}

export const propHref: PropsItemType = {
    subtitle: "href",
    intro: (
        <>
            To use this component as an <code>a</code> tag, for external links.
        </>
    ),
    type: "string",
}

export const propBlank: PropsItemType = {
    subtitle: "blank",
    intro: (
        <>
            Only when using <code>to</code> or <code>href</code>props. To add a{" "}
            <code>target="_blank"</code>.
        </>
    ),
    type: "boolean",
}

export const propAllLinks: PropsItemType[] = [propTo, propHref, propBlank]

export const propAllLinksClickRequired: PropsItemType[] = [
    {
        subtitle: "onClick",
        intro: (
            <>
                Either <code>onClick</code>, <code>to</code> or{" "}
                <code>href</code> is required. But only one of them is possible.
            </>
        ),
        type: "function",
    },
    {
        subtitle: "to",
        intro: (
            <>
                To use this component as a{" "}
                <a
                    href="https://reactrouter.com/en/main/components/link"
                    target="_blank"
                    rel="noreferrer noopener"
                >
                    React Router Link
                </a>
                . Either <code>onClick</code>, <code>to</code> or{" "}
                <code>href</code> is required. But only one of them is possible.
            </>
        ),
        type: "string",
    },
    {
        subtitle: "href",
        intro: (
            <>
                To use this component as an <code>a</code> tag, for external
                links. Either <code>onClick</code>, <code>to</code> or{" "}
                <code>href</code> is required. But only one of them is possible.
            </>
        ),
        type: "string",
    },
    {
        subtitle: "blank",
        intro: (
            <>
                Only when using <code>to</code> or <code>href</code>props. To
                add a <code>target="_blank"</code>.
            </>
        ),
        type: "boolean",
    },
]

export const borderStyles = [
    "dotted",
    "dashed",
    "solid",
    "double",
    "groove",
    "ridge",
    "inset",
    "outset",
    "none",
    "hidden",
]

export const borderStyleDefault = (
    <>
        If <code>borderColor</code> or <code>borderWidth</code> is set:{" "}
        <code>solid</code>.
    </>
)

export const borderObject = (separator?: SeparatorType) => {
    const object: PropsItemType[] = [
        returnSeparator(separator),
        {
            subtitle: "border",
            type: "object",
        },
        {
            title: (
                <>
                    <code>border</code> object
                </>
            ),
            subtitle: "style",
            type: "string",
            possible: borderStyles,
            default: (
                <>
                    if <code>color</code> or <code>width</code> are set:{" "}
                    <code>solid</code>.
                </>
            ),
        },
        {
            subtitle: "width",
            type: "number",
            default: (
                <>
                    if <code>color</code> or <code>style</code> are set:{" "}
                    <code>1</code>.
                </>
            ),
        },
        {
            subtitle: "color",
            type: "string",
            possibleString: commonProps.allColors,
            default: (
                <>
                    if <code>width</code> or <code>style</code> are set:{" "}
                    <code>gray-200</code>.
                </>
            ),
        },
    ]

    return object
}
